import {
  type PlaidLinkOnEvent,
  type PlaidLinkOnExit,
  type PlaidLinkOnExitMetadata,
  PlaidLinkStableEvent,
} from "react-plaid-link";

import { toastError } from "@alpacahq/ui/old";

import {
  addPlaidErrorBreadcrumb,
  capturePlaidError,
  trackPlaidEvent,
} from "./plaidTelemetry";

export interface PlaidEventHandlersArgs {
  accountID: string;
  /** User exited Link with no error (cancel / completed handoff). */
  onExitClean: () => void;
  /** Generic Plaid error after the shared capture + toast have run. */
  onExitError: () => void;
  /**
   * Optional cleanup run at the very top of onExit (before any branching), e.g.
   * forgetting an in-flight OAuth resume session so a stale token is never
   * reused on a later attempt.
   */
  beforeExit?: () => void;
  /**
   * Optional special-case for an expired/invalid persisted token on OAuth
   * return. When provided, an INVALID_LINK_TOKEN exit routes here instead of the
   * generic error path. The fresh-link page omits this.
   */
  onInvalidLinkToken?: (metadata: PlaidLinkOnExitMetadata) => void;
}

/**
 * Builds the `onEvent` / `onExit` handlers shared by both ACH link pages so
 * lifecycle telemetry and error reporting stay identical. `onSuccess` is NOT
 * built here — it is the money path and lives in `useSubmitPlaidRelationship`.
 */
export const createPlaidEventHandlers = ({
  accountID,
  onExitClean,
  onExitError,
  beforeExit,
  onInvalidLinkToken,
}: PlaidEventHandlersArgs): {
  onEvent: PlaidLinkOnEvent;
  onExit: PlaidLinkOnExit;
} => {
  const onExit: PlaidLinkOnExit = (error, metadata) => {
    beforeExit?.();

    if (!error) {
      onExitClean();
      return;
    }

    // Expired/invalid persisted token on OAuth return: fully exit resume mode
    // instead of stranding the user on the dead token.
    if (error.error_code === "INVALID_LINK_TOKEN" && onInvalidLinkToken) {
      onInvalidLinkToken(metadata);
      return;
    }

    capturePlaidError(accountID, `Plaid Link error: ${error.error_code}`, {
      error,
      metadata,
      linkSessionId: metadata.link_session_id,
    });
    toastError(`Plaid encountered an error: ${error.error_code}`);
    onExitError();
  };

  const onEvent: PlaidLinkOnEvent = (eventName, metadata) => {
    let eventData: Record<string, unknown> | undefined;
    switch (eventName) {
      case PlaidLinkStableEvent.OPEN: {
        eventData = {
          event: PlaidLinkStableEvent.OPEN,
          view_name: metadata.view_name,
        };
        break;
      }
      case PlaidLinkStableEvent.HANDOFF: {
        eventData = {
          event: PlaidLinkStableEvent.HANDOFF,
          view_name: metadata.institution_name,
        };
        break;
      }
      case PlaidLinkStableEvent.EXIT: {
        eventData = {
          event: PlaidLinkStableEvent.EXIT,
          exit_status: metadata.exit_status,
          error_code: metadata.error_code,
          error_type: metadata.error_type,
          institution_name: metadata.institution_name,
        };
        break;
      }
      case PlaidLinkStableEvent.ERROR: {
        eventData = {
          event: PlaidLinkStableEvent.ERROR,
          error_code: metadata.error_code,
          error_message: metadata.error_message,
          error_type: metadata.error_type,
          institution_name: metadata.institution_name,
        };
        // Breadcrumb only — we do NOT toast here: ERROR also fires for in-Link
        // recoverable errors (e.g. bad bank credentials the user retries), so
        // user-facing errors are surfaced once in onExit instead.
        addPlaidErrorBreadcrumb(metadata);
        break;
      }
      // OPEN_OAUTH / FAIL_OAUTH are not in the PlaidLinkStableEvent enum (they
      // arrive as raw strings), but they are the OAuth handoff/failure events
      case "OPEN_OAUTH": {
        eventData = {
          event: "OPEN_OAUTH",
          view_name: metadata.view_name,
          institution_name: metadata.institution_name,
        };
        break;
      }
      case "FAIL_OAUTH": {
        eventData = {
          event: "FAIL_OAUTH",
          error_code: metadata.error_code,
          error_type: metadata.error_type,
          error_message: metadata.error_message,
          institution_name: metadata.institution_name,
        };
        break;
      }
      default:
        break;
    }

    if (eventData) {
      trackPlaidEvent(eventData);
    }
  };

  return { onEvent, onExit };
};
